import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Pencil, X } from 'lucide-react';
import { products } from '../api';
import ProduktApplicationsBereich from '../components/ProduktApplicationsBereich';
import StatusBadge from '../components/StatusBadge';
import type {
  Product,
  ProductPlan,
  ProductStatus,
  PlanStatus,
  WooCommerceVisibility } from '../types';

type Tab = 'plans' | 'applications';

interface PlanForm {
  name: string;
  slug: string;
  monthlyPrice: string;
  yearlyPrice: string;
  slotWeight: string;
  status: PlanStatus;
}

const DEFAULT_PLAN_FORM: PlanForm = {
  name: '',
  slug: '',
  monthlyPrice: '',
  yearlyPrice: '',
  slotWeight: '1',
  status: 'ACTIVE',
};

interface AttributeRow {
  name: string;
  optionsText: string;
}

interface ProductForm {
  name: string;
  description: string;
  imageUrl: string;
  status: ProductStatus;
  attributes: AttributeRow[];
}

function toProductForm(product: Product): ProductForm {
  return {
    name: product.name,
    description: product.description ?? '',
    imageUrl: product.imageUrl ?? '',
    status: product.status,
    attributes: (product.extraAttributes ?? []).map((a) => ({
      name: a.name,
      optionsText: a.options.join(', '),
    })),
  };
}

export default function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('plans');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [planForm, setPlanForm] = useState<PlanForm>(DEFAULT_PLAN_FORM);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [productForm, setProductForm] = useState<ProductForm | null>(null);
  const [formError, setFormError] = useState('');

  const { data: product, isLoading: loadingProduct } = useQuery({
    queryKey: ['products', id],
    queryFn: () => products.get(id!),
    enabled: !!id,
  });

  const { data: plansList, isLoading: loadingPlans } = useQuery({
    queryKey: ['products', id, 'plans'],
    queryFn: () => products.getPlans(id!),
    enabled: !!id && activeTab === 'plans',
  });

  const createPlanMutation = useMutation({
    mutationFn: (payload: Partial<ProductPlan>) => products.createPlan(id!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products', id, 'plans'] });
      setShowPlanModal(false);
      setPlanForm(DEFAULT_PLAN_FORM);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to create plan.';
      setFormError(msg);
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: (payload: Partial<ProductPlan>) =>
      products.updatePlan(id!, editingPlanId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products', id, 'plans'] });
      setShowPlanModal(false);
      setEditingPlanId(null);
      setPlanForm(DEFAULT_PLAN_FORM);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to update plan.';
      setFormError(msg);
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: (payload: Partial<Product> & { extraAttributes?: { name: string; options: string[] }[] }) =>
      products.update(id!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products', id] });
      setShowProductModal(false);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to update product.';
      setFormError(msg);
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: (visibility: WooCommerceVisibility | null) =>
      products.setWooCommerceVisibility(id!, visibility),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products', id] });
    },
  });

  if (loadingProduct) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-4 bg-slate-100 rounded w-96" />
      </div>
    );
  }

  if (!product) {
    return <div className="text-slate-500">{t('products.notFound')}</div>;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'plans', label: 'Plans' },
    // Vorlagen und Felder gehoeren seit Stufe 2 der Application; hier steht,
    // welche Applications das Produkt verkauft und mit welchen Vorgaben.
    { key: 'applications', label: (product.applications?.length ?? 0) > 1 ? `Applications (${product.applications!.length})` : 'Application' },
  ];

  function slugify(s: string) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/products')}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
        >
          <ArrowLeft size={14} /> Back to Products
        </button>
        <div className="flex items-start justify-between">
          <div className="flex gap-4">
            {product.imageUrl && (
              <img
                src={product.imageUrl}
                alt=""
                className="w-16 h-16 rounded-lg object-cover border border-slate-200"
              />
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{product.name}</h1>
                <StatusBadge status={product.status} type="product" />
              </div>
              <p className="text-slate-500 text-sm mt-1">
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{product.slug}</code>
                {product.description && <span className="ml-2">{product.description}</span>}
              </p>
              {product.extraAttributes?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {product.extraAttributes.map((a) => (
                    <span
                      key={a.name}
                      className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                    >
                      {a.name}: {a.options.join(', ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => {
              setProductForm(toProductForm(product));
              setFormError('');
              setShowProductModal(true);
            }}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 px-3 py-1.5 rounded-lg"
          >
            <Pencil size={14} /> Edit
          </button>
        </div>

        {/* WooCommerce sync status */}
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <span className="font-medium text-slate-600">WooCommerce:</span>
          {product.woocommerceProductId ? (
            <span>Product #{product.woocommerceProductId}</span>
          ) : (
            <span className="text-slate-400 italic">{t('products.notSynced')}</span>
          )}
          {product.wcSyncError && (
            <span className="text-red-600">sync failed: {product.wcSyncError}</span>
          )}
          {!product.wcSyncError && product.wcSyncedAt && (
            <span>synced {new Date(product.wcSyncedAt).toLocaleString()}</span>
          )}
          <span className="mx-1 text-slate-300">|</span>
          <label className="flex items-center gap-1.5">
            Visibility:
            <select
              value={product.wcVisibilityOverride ?? ''}
              disabled={visibilityMutation.isPending}
              onChange={(e) =>
                visibilityMutation.mutate(
                  (e.target.value || null) as WooCommerceVisibility | null,
                )
              }
              className="border border-slate-300 rounded px-1.5 py-1 text-xs"
            >
              <option value="">Auto (from status: {product.status === 'ACTIVE' ? 'publish' : 'draft'})</option>
              <option value="PUBLISH">Published</option>
              <option value="DRAFT">Draft</option>
              <option value="PRIVATE">Private</option>
            </select>
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Plans tab */}
      {activeTab === 'plans' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditingPlanId(null);
                setPlanForm(DEFAULT_PLAN_FORM);
                setFormError('');
                setShowPlanModal(true);
              }}
              className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
            >
              <Plus size={14} /> Add Plan
            </button>
          </div>
          {loadingPlans ? (
            <div className="space-y-2 animate-pulse">
              {[1, 2].map((i) => <div key={i} className="h-16 bg-slate-100 rounded-lg" />)}
            </div>
          ) : plansList?.length === 0 ? (
            <p className="text-slate-400 italic text-sm">{t('products.noPlans')}</p>
          ) : (
            <div className="grid gap-3">
              {plansList?.map((plan) => (
                <div key={plan.id} className="bg-surface border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{plan.name}</span>
                      <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{plan.slug}</code>
                      <StatusBadge status={plan.status} type="plan" />
                    </div>
                    <div className="flex gap-4 mt-1 text-xs text-slate-500">
                      {plan.monthlyPrice && <span>€{plan.monthlyPrice}/mo</span>}
                      {plan.yearlyPrice && <span>€{plan.yearlyPrice}/yr</span>}
                      <span>Slot weight: {plan.slotWeight}</span>
                      {plan.woocommerceVariationIdMonthly && (
                        <span>WC Var (monthly): {plan.woocommerceVariationIdMonthly}</span>
                      )}
                      {plan.woocommerceVariationIdYearly && (
                        <span>WC Var (yearly): {plan.woocommerceVariationIdYearly}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setEditingPlanId(plan.id);
                      setPlanForm({
                        name: plan.name,
                        slug: plan.slug,
                        monthlyPrice: plan.monthlyPrice ?? '',
                        yearlyPrice: plan.yearlyPrice ?? '',
                        slotWeight: String(plan.slotWeight),
                        status: plan.status,
                      });
                      setFormError('');
                      setShowPlanModal(true);
                    }}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 font-medium"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Applications des Produkts (Stufe 2) */}
      {activeTab === 'applications' && <ProduktApplicationsBereich product={product} />}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md max-h-screen overflow-y-auto">
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface">
              <h2 className="text-base font-semibold">{editingPlanId ? 'Edit Plan' : 'Add Plan'}</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFormError('');
                const payload = {
                  name: planForm.name,
                  slug: planForm.slug,
                  monthlyPrice: planForm.monthlyPrice || undefined,
                  yearlyPrice: planForm.yearlyPrice || undefined,
                  slotWeight: parseFloat(planForm.slotWeight),
                  status: planForm.status,
                } as Partial<ProductPlan>;
                if (editingPlanId) {
                  updatePlanMutation.mutate(payload);
                } else {
                  createPlanMutation.mutate(payload);
                }
              }}
              className="px-6 py-5 space-y-4"
            >
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{formError}</div>
              )}
              {[
                { label: 'Name', key: 'name', required: true },
                { label: 'Slug', key: 'slug', required: true, mono: true },
                { label: 'Monthly Price (€)', key: 'monthlyPrice' },
                { label: 'Yearly Price (€)', key: 'yearlyPrice' },
                { label: 'Slot Weight', key: 'slotWeight' },
              ].map(({ label, key, required, mono }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                  <input
                    required={required}
                    value={planForm[key as keyof PlanForm]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPlanForm((f) => ({
                        ...f,
                        [key]: val,
                        ...(key === 'name' && !editingPlanId ? { slug: slugify(val) } : {}),
                      }));
                    }}
                    className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${mono ? 'font-mono' : ''}`}
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={planForm.status}
                  onChange={(e) => setPlanForm((f) => ({ ...f, status: e.target.value as PlanStatus }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="DEPRECATED">Deprecated</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowPlanModal(false); setEditingPlanId(null); }}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createPlanMutation.isPending || updatePlanMutation.isPending}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60"
                >
                  {editingPlanId
                    ? updatePlanMutation.isPending ? 'Saving…' : 'Save'
                    : createPlanMutation.isPending ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product Edit Modal */}
      {showProductModal && productForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface">
              <h2 className="text-base font-semibold">{t('products.editProduct')}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t('products.syncHint')}</p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFormError('');
                const attributes = productForm.attributes
                  .map((a) => ({
                    name: a.name.trim(),
                    options: a.optionsText.split(',').map((o) => o.trim()).filter(Boolean),
                  }))
                  .filter((a) => a.name && a.options.length > 0);
                updateProductMutation.mutate({
                  name: productForm.name,
                  description: productForm.description,
                  imageUrl: productForm.imageUrl,
                  status: productForm.status,
                  extraAttributes: attributes,
                });
              }}
              className="px-6 py-5 space-y-4"
            >
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  required
                  value={productForm.name}
                  onChange={(e) => setProductForm((f) => f && { ...f, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={productForm.description}
                  onChange={(e) => setProductForm((f) => f && { ...f, description: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('products.imageUrl')}</label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={productForm.imageUrl}
                  onChange={(e) => setProductForm((f) => f && { ...f, imageUrl: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                {productForm.imageUrl && (
                  <img src={productForm.imageUrl} alt="" className="mt-2 w-20 h-20 rounded-lg object-cover border border-slate-200" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={productForm.status}
                  onChange={(e) => setProductForm((f) => f && { ...f, status: e.target.value as ProductStatus })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="DEPRECATED">Deprecated</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-slate-700">
                    Extra WooCommerce Attributes
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setProductForm((f) => f && { ...f, attributes: [...f.attributes, { name: '', optionsText: '' }] })
                    }
                    className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
                  >
                    <Plus size={12} /> Add attribute
                  </button>
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  Shown on the product page (not variation-defining) — e.g. "Zielgruppe: KMU, Enterprise".
                  Plan and Abrechnung are managed automatically from the plans below.
                </p>
                <div className="space-y-2">
                  {productForm.attributes.map((attr, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        placeholder="Name"
                        value={attr.name}
                        onChange={(e) =>
                          setProductForm((f) => {
                            if (!f) return f;
                            const attributes = [...f.attributes];
                            attributes[i] = { ...attributes[i], name: e.target.value };
                            return { ...f, attributes };
                          })
                        }
                        className="w-1/3 border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <input
                        placeholder={t('products.optionsCommaSeparated')}
                        value={attr.optionsText}
                        onChange={(e) =>
                          setProductForm((f) => {
                            if (!f) return f;
                            const attributes = [...f.attributes];
                            attributes[i] = { ...attributes[i], optionsText: e.target.value };
                            return { ...f, attributes };
                          })
                        }
                        className="flex-1 border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setProductForm((f) => f && { ...f, attributes: f.attributes.filter((_, j) => j !== i) })
                        }
                        className="text-slate-400 hover:text-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateProductMutation.isPending}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60"
                >
                  {updateProductMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
